import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Trash2, RefreshCw } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent } from '@/components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Modal } from '@/components/ui/Modal';
import { createContaGabaritoInput } from '@portal-leo/shared';
import type { z } from 'zod';

type CreateContaGabaritoInput = z.infer<typeof createContaGabaritoInput>;

interface GabaritoViewProps {
  eventoId: string;
}

export function GabaritoView({ eventoId }: GabaritoViewProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const utils = trpc.useUtils();

  const { data: gabaritoData, isLoading } = trpc.livroCaixa.listGabarito.useQuery({ eventoId, page: 1, limit: 500 });
  const gabarito = gabaritoData?.items;

  const invalidateRelatedQueries = () => {
    utils.livroCaixa.listGabarito.invalidate({ eventoId });
    utils.previsaoOrcamentaria.getComparativo.invalidate({ eventoId });
    utils.previsaoOrcamentaria.getResumoPrevisao.invalidate({ eventoId });
    utils.livroCaixa.getBalancete.invalidate({ eventoId });
    utils.livroCaixa.getResumoFinanceiro.invalidate({ eventoId });
  };

  const addMutation = trpc.livroCaixa.addGabarito.useMutation({
    onSuccess: () => {
      invalidateRelatedQueries();
      handleCloseModal();
    },
    onError: (error) => {
      alert('Erro: ' + error.message);
    },
  });

  const removeMutation = trpc.livroCaixa.removeGabarito.useMutation({
    onSuccess: () => {
      invalidateRelatedQueries();
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  const seedMutation = trpc.livroCaixa.seedGabaritoPadrao.useMutation({
    onSuccess: () => {
      invalidateRelatedQueries();
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  const updateMutation = trpc.livroCaixa.updateGabarito.useMutation({
    onSuccess: () => {
      invalidateRelatedQueries();
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateContaGabaritoInput>({
    resolver: zodResolver(createContaGabaritoInput),
    defaultValues: { eventoId, natureza: 'RECEITA' },
  });

  const onSubmit = (data: CreateContaGabaritoInput) => {
    addMutation.mutate(data);
  };

  const handleOpenModal = () => {
    reset({ eventoId, nome: '', natureza: 'RECEITA' });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    reset();
  };

  const handleRemove = (id: string) => {
    if (window.confirm('Tem certeza que deseja remover esta conta?')) {
      removeMutation.mutate({ id });
    }
  };

  const handleSeed = () => {
    if (window.confirm('Isso criará as 21 contas padrão do LEO. Deseja continuar?')) {
      seedMutation.mutate({ eventoId });
    }
  };

  const handleParentChange = (id: string, parentId: string | null) => {
    updateMutation.mutate({ id, parentId });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  // Sort: parents first, children indented below
  const sortedByParent = (items: typeof gabarito) => {
    if (!items) return [];
    const parents = items.filter((g) => !g.parentId);
    const childrenMap = new Map<string, typeof items>();
    for (const g of items) {
      if (g.parentId) {
        const siblings = childrenMap.get(g.parentId) || [];
        siblings.push(g);
        childrenMap.set(g.parentId, siblings);
      }
    }
    const result: (typeof items[number] & { isChild?: boolean })[] = [];
    for (const p of parents) {
      result.push(p);
      const children = childrenMap.get(p.id);
      if (children) {
        for (const c of children) {
          result.push({ ...c, isChild: true });
        }
      }
    }
    return result;
  };

  // Get potential parents for a given conta (same natureza, not a child, not itself)
  const getPotentialParents = (conta: NonNullable<typeof gabarito>[number]) => {
    if (!gabarito) return [];
    return gabarito.filter(
      (g) =>
        g.id !== conta.id &&
        g.natureza === conta.natureza &&
        !g.parentId && // can't be a child itself
        conta.childCount === 0 // current conta must not have children to become a child
    );
  };

  const receitas = sortedByParent(gabarito?.filter((g) => g.natureza === 'RECEITA'));
  const despesas = sortedByParent(gabarito?.filter((g) => g.natureza === 'DESPESA'));

  const renderTable = (items: ReturnType<typeof sortedByParent>, natureza: 'RECEITA' | 'DESPESA') => {
    const bgHeader = natureza === 'RECEITA' ? 'bg-green-50' : 'bg-red-50';
    const borderHeader = natureza === 'RECEITA' ? 'border-green-200' : 'border-red-200';
    const textHeader = natureza === 'RECEITA' ? 'text-green-800' : 'text-red-800';
    const label = natureza === 'RECEITA' ? 'Receitas' : 'Despesas';
    const rawCount = gabarito?.filter((g) => g.natureza === natureza).length || 0;

    return (
      <Card className="border border-gray-200">
        <CardContent className="p-0">
          <div className={`${bgHeader} px-4 py-2 border-b ${borderHeader}`}>
            <h4 className={`text-sm font-semibold ${textHeader}`}>{label} ({rawCount})</h4>
          </div>
          {items.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Código</TableHead>
                  <TableHead>Conta</TableHead>
                  <TableHead className="w-48">Conta Pai</TableHead>
                  <TableHead className="text-right w-16">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((g) => {
                  const isChild = 'isChild' in g && g.isChild;
                  const potentialParents = getPotentialParents(g);
                  const isParent = g.childCount > 0;

                  return (
                    <TableRow key={g.id} className={isChild ? 'bg-gray-50/50' : ''}>
                      <TableCell className="text-gray-500">{g.codigo}</TableCell>
                      <TableCell className="font-medium">
                        {isChild && <span className="text-gray-400 mr-1">└─</span>}
                        {g.nome}
                        {isParent && (
                          <span className="ml-2 text-xs text-indigo-600 font-normal">
                            [Agrupadora: {g.childCount} conta{g.childCount > 1 ? 's' : ''}]
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {/* Only show select if this conta has no children (not a parent) */}
                        {!isParent ? (
                          <select
                            value={g.parentId || ''}
                            onChange={(e) => handleParentChange(g.id, e.target.value || null)}
                            disabled={updateMutation.isPending}
                            className="w-full rounded border border-gray-200 px-2 py-1 text-xs focus:border-primary-500 focus:outline-none"
                          >
                            <option value="">— Nenhum —</option>
                            {potentialParents.map((p) => (
                              <option key={p.id} value={p.id}>{p.codigo} - {p.nome}</option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleRemove(g.id)} disabled={removeMutation.isPending}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <p className="px-4 py-3 text-sm text-gray-400">
              Nenhuma conta de {natureza === 'RECEITA' ? 'receita' : 'despesa'}
            </p>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{gabarito?.length || 0} contas cadastradas</p>
        <div className="flex gap-2">
          {(!gabarito || gabarito.length === 0) && (
            <Button variant="outline" onClick={handleSeed} isLoading={seedMutation.isPending}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Carregar Padrão
            </Button>
          )}
          <Button onClick={handleOpenModal}>
            <Plus className="mr-2 h-4 w-4" />
            Nova Conta
          </Button>
        </div>
      </div>

      {renderTable(receitas, 'RECEITA')}
      {renderTable(despesas, 'DESPESA')}

      {/* Modal */}
      <Modal isOpen={isModalOpen} onClose={handleCloseModal} title="Nova Conta">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <input type="hidden" {...register('eventoId')} />

          <Input label="Nome da Conta" {...register('nome')} error={errors.nome?.message} placeholder="Ex: Inscrições Lote 1" />

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Natureza</label>
            <select
              {...register('natureza')}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="RECEITA">Receita</option>
              <option value="DESPESA">Despesa</option>
            </select>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={handleCloseModal}>Cancelar</Button>
            <Button type="submit" isLoading={addMutation.isPending}>Adicionar</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
